import {
  Scalars,
  User,
  User_Role,
  User_SortField,
  User_SortOrder,
  User_Status,
  User_UpdateInput,
  User_WhereInput,
  UsersDocument,
  useUser_UpdateMutation,
  useUsersLazyQuery,
} from '@/graphql/codegen/generated/graphql';
import { computed, inject, ref, watch } from 'vue';
import { useIncrediForm } from 'incredi-ui';
import { validateEmail, validateName, validatePassword, validateUserName } from '@/utils/string-validations';
import { useI18n } from 'vue-i18n';
import { IKOpenDialog } from '@/types/injectionKeys';
import { useNotify } from '@/services/notify/notify';
import { CONST_NOTIFY } from '@/services/notify/CONST';
import apolloClient from '@/plugins/GraphQL/Apollo/client';
import { ErrorCodes } from '@/const/errorCodes';
import { useDialogPresets } from '@/services/dialogPresets.composable';
import { dateUtils } from '@/services/dateUtils';
import { useLazyQueryAfterLogin } from '@/services/graphql.utils';
import { IKApplicationPersistentData } from '@/services/persistentDataProvider/persistentDataProvider.types';
import { app } from '@/bootloader';
import { MAP_COLUMNS_To_User_SortField } from './components/users-table.const';
import { IKUsersPage } from './users.injectionKeys';

export const useUsersActions = () => {
  const { mutate: updateUser } = useUser_UpdateMutation({});
  const setUpdateUserAction = async (userId: Scalars['Int'], data: User_UpdateInput) =>
    updateUser({
      id: userId,
      data: {
        ...data,
      },
    });

  return { setUpdateUserAction };
};

export const useUsersTableFilter = () => {
  type UsersPagePersistentDataType = {
    sort: User_WhereInput['sort'];
    status: User_Status | 'All';
  };

  const usersPersistentDataSection = 'UsersPage';

  const appPersistentData = inject(IKApplicationPersistentData);

  const defaultSort = {
    field: User_SortField.Id,
    order: User_SortOrder.Descending,
  };

  const filters = {
    search: ref(''),
    status: ref<User_Status | 'All'>('All'),
    sort: ref<User_WhereInput['sort']>(defaultSort),
  };

  watch(
    () => appPersistentData?.GQL?.networkStatus?.value,
    () => {
      const { status, sort } = appPersistentData?.getPersistentData(usersPersistentDataSection) as UsersPagePersistentDataType;
      filters.status.value = status ? (status as User_Status) : 'All';
      filters.sort.value = sort ? (sort as User_WhereInput['sort']) : defaultSort;
    },
    {
      immediate: true,
    },
  );

  const setPersistentUsersFilter = () => {
    appPersistentData?.setPersistentData(usersPersistentDataSection, {
      status: filters.status.value,
      sort: filters.sort.value,
    } as UsersPagePersistentDataType);
  };

  const setSearch = (newValue: string) => {
    filters.search.value = newValue;
  };
  const setStatus = (newValue: User_Status | 'All') => {
    filters.status.value = newValue;
    setPersistentUsersFilter();
  };
  const setSort = (newValue: User_WhereInput['sort']) => {
    filters.sort.value = newValue;
    setPersistentUsersFilter();
  };
  return {
    search: {
      set: setSearch,
      get: filters.search,
    },
    status: {
      set: setStatus,
      get: filters.status,
    },
    sort: {
      set: setSort,
      get: filters.sort,
    },
    clear() {
      filters.search.value = '';
      filters.status.value = 'All';
      filters.sort.value = {
        field: User_SortField.Id,
        order: User_SortOrder.Descending,
      };
    },
  };
};

export const useUsersTable = () => {
  const usersGql = useLazyQueryAfterLogin(useUsersLazyQuery);
  const usersFilter = useUsersTableFilter();
  const sortUsers = (userA: string | undefined, userB: string | undefined) => Number(userA?.localeCompare(String(userB)));

  const users = computed(
    () =>
      usersGql.result.value?.users
        ?.filter(user => {
          const searchQueryLower = usersFilter?.search.get.value.toLowerCase() || '';
          const numOfSpacesInSearchVal = searchQueryLower.trim().split(' ').length;
          const searchByRoleQuery = numOfSpacesInSearchVal === 2 ? searchQueryLower.replace(' ', '') : searchQueryLower;
          return (
            user?.userName?.toLowerCase().includes(searchQueryLower) ||
            user?.additionalInfo?.firstName?.toLowerCase().includes(searchQueryLower) ||
            user?.additionalInfo?.lastName?.toLowerCase().includes(searchQueryLower) ||
            user?.additionalInfo?.email?.toLowerCase().includes(searchQueryLower) ||
            user?.role.toLocaleLowerCase().includes(searchByRoleQuery) ||
            user?.status?.toLocaleLowerCase().includes(searchQueryLower) ||
            user?.id.toString().includes(searchQueryLower) ||
            dateUtils.getFormattedDate(new Date(user?.lastLogin)).includes(searchQueryLower)
          );
        })
        .filter(user => {
          const userStatus = usersFilter?.status.get.value;
          return userStatus === 'All' || user?.status === userStatus;
        })
        .sort((userA, userB) => {
          const sort = usersFilter?.sort.get.value;
          if (sort?.field === MAP_COLUMNS_To_User_SortField.ID) {
            return sort?.order === User_SortOrder.Ascending ? Number(userA?.id) - Number(userB?.id) : Number(userB?.id) - Number(userA?.id);
          }
          if (sort?.field === MAP_COLUMNS_To_User_SortField.Username) {
            return sort?.order === User_SortOrder.Ascending ? sortUsers(userA?.userName, userB?.userName) : sortUsers(userB?.userName, userA?.userName);
          }

          if (sort?.field === MAP_COLUMNS_To_User_SortField.Name) {
            const userAFullName = `${userA?.additionalInfo?.firstName || ''}${userA?.additionalInfo?.lastName || ''}`;
            if (!userAFullName) {
              return 1;
            }
            const userBFullName = `${userB?.additionalInfo?.firstName || ''}${userB?.additionalInfo?.lastName || ''}`;
            if (!userBFullName) {
              return -1;
            }
            return sort?.order === User_SortOrder.Ascending ? sortUsers(userAFullName, userBFullName) : sortUsers(userBFullName, userAFullName);
          }

          if (sort?.field === MAP_COLUMNS_To_User_SortField.Role) {
            return sort?.order === User_SortOrder.Ascending ? sortUsers(userA?.role, userB?.role) : sortUsers(userB?.role, userA?.role);
          }
          if (sort?.field === MAP_COLUMNS_To_User_SortField.Status) {
            return sort?.order === User_SortOrder.Ascending
              ? sortUsers(userA?.status as string | undefined, userB?.status as string | undefined)
              : sortUsers(userB?.status as string | undefined, userA?.status as string | undefined);
          }
          if (sort?.field === MAP_COLUMNS_To_User_SortField['Last Login']) {
            return sort?.order === User_SortOrder.Ascending
              ? Number(new Date(userA?.lastLogin).getTime() - new Date(userB?.lastLogin).getTime())
              : Number(new Date(userB?.lastLogin).getTime() - new Date(userA?.lastLogin).getTime());
          }
          return 0;
        }) || [],
  );

  const selectedUserId = ref<number>();
  const selectedUser = computed<User | undefined>(() => usersGql.result.value?.users?.find(u => u?.id === selectedUserId.value) || undefined);

  const selectedUserIndex = computed<number | null>(() => {
    const index = usersGql.result.value?.users?.findIndex(u => u?.id === selectedUser.value?.id);
    return typeof index === 'number' ? index : null;
  });

  const setSelectedUser = (user: User) => {
    selectedUserId.value = user.id;
  };
  return {
    users,
    usersGql,
    filters: usersFilter,
    selectedUser: {
      clear: () => {
        selectedUserId.value = undefined;
      },
      set: setSelectedUser,
      get: {
        index: selectedUserIndex,
        userData: selectedUser,
      },
    },
  };
};
const useEditUserForm = () => {
  const { t } = useI18n();
  const initialValues = {
    userName: '',
    email: '',
    firstName: '',
    lastName: '',
    role: User_Role.Viewer,
    status: User_Status.Active,
    password: '',
    confirmPassword: '',
  };
  const { form, fields } = useIncrediForm({
    initialValues,
    validationSchema: {
      password: (v: string) => !v || validatePassword(v) || t('users.userFormRules.password'),
      confirmPassword: (v: string) => {
        if (form.values.password && !v) {
          return t('users.userFormRules.required');
        }
        return (v as string) === form.values.password || t('users.userFormRules.confirmPassword');
      },
      email: (v: string) => !v || validateEmail(v) || 'Invalid email format',
      userName: (v: string) => validateUserName(v) || t('users.userFormRules.userName'),
      firstName: (v: string) => !v || validateName(v) || t('users.userFormRules.invalid'),
      lastName: (v: string) => !v || validateName(v) || t('users.userFormRules.invalid'),
    },
  });

  const resetFormToInitialState = () => {
    form.resetForm({
      values: initialValues,
    });
  };

  return { form, resetFormToInitialState, fields };
};

const useMutateUserGQL = (editUserForm: ReturnType<typeof useEditUserForm>, usersTable: ReturnType<typeof useUsersTable>) => {
  const { t } = useI18n();
  const { toastMessage } = useNotify();
  const { mutate } = useUser_UpdateMutation({});
  return async (data: User_UpdateInput) => {
    if (!usersTable.selectedUser.get.userData.value?.id) return;
    try {
      const result = await mutate({
        id: usersTable.selectedUser.get.userData.value?.id,
        data,
      });
      apolloClient.writeQuery({
        query: UsersDocument,
        variables: {
          filter: {},
        },
        data: { users: usersTable.usersGql.result.value?.users?.map(user => (user?.id === result?.data?.user_update?.id ? (result?.data?.user_update as typeof user) : user)) },
      });
      return true;
    } catch (error) {
      const code = (error as any).graphQLErrors[0].extensions.applicationErrorCode;
      switch (code) {
        case ErrorCodes.USER_NAME_ALREADY_EXIST:
          editUserForm.form.setErrors({ userName: t('users.notUniqueUserName') });
          return false;
        default:
          toastMessage(t('usersTable.userEdit.fail'), CONST_NOTIFY.MESSAGE_TYPE.ERROR);
          return false;
      }
    }
  };
};

const useUserEditorFormSubmit = (editUserForm: ReturnType<typeof useEditUserForm>, usersTable: ReturnType<typeof useUsersTable>) => {
  const { toastMessage } = useNotify();
  const { t } = useI18n();

  const mutateUser = useMutateUserGQL(editUserForm, usersTable);
  return editUserForm.form.handleSubmit(async () => {
    const { values } = editUserForm.form;
    const users = usersTable.usersGql.result.value?.users || [];
    const gridAdminCount = users.filter(user => user?.role === User_Role.GridAdmin && user.status !== User_Status.Inactive);
    const isEditedUserIsTheOnlyGridAdmin = gridAdminCount.length === 1 && values.role === User_Role.GridAdmin;
    if (isEditedUserIsTheOnlyGridAdmin && values.role !== User_Role.GridAdmin) {
      toastMessage(t('usersTable.userRole.lastAdmin'), CONST_NOTIFY.MESSAGE_TYPE.ERROR);
      return;
    }
    const data: User_UpdateInput = {
      userName: values.userName,
      additionalInfo: {
        email: values.email || null, // || null for backened validation.
        firstName: values.firstName || null,
        lastName: values.lastName || null,
      },
      role: values.role,
      status: values.status,
      password: values.password,
    };
    if (!values.password) {
      delete data.password;
    }
    const userUpdateSuccess = await mutateUser(data);
    if (userUpdateSuccess) {
      toastMessage(t('usersTable.userEdit.success'), CONST_NOTIFY.MESSAGE_TYPE.SUCCESS);
      editUserForm.form.resetForm({
        values: {
          userName: values.userName,
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          role: values.role,
          status: values.status,
          password: '',
          confirmPassword: '',
        },
      });
    }
    return userUpdateSuccess;
  });
};
const useEditUserConfirmationModal = (submitUserEditorForm: ReturnType<typeof useUserEditorFormSubmit>) => {
  const openDialog = inject(IKOpenDialog);
  const { closeDialog } = useDialogPresets();

  const { t } = useI18n();
  return async () =>
    new Promise<{
      ok: boolean;
      cancel: boolean;
      formSuccessfullySubmitted: boolean;
    }>((resolve, reject) => {
      if (openDialog)
        openDialog('SaveChangesConfirmationDialog', {
          width: '432px',
          height: '214px',
          grayBackground: true,
          dialogTitle: t('users.okCancelDialog.title'),
          body: t('users.okCancelDialog.body'),
          okLabel: t('users.okCancelDialog.okLabel'),
          cancelLabel: t('users.okCancelDialog.cancelLabel'),
          iconClose: true,
          okCallback: async () => {
            const formSuccessfullySubmitted = await submitUserEditorForm();
            closeDialog();
            resolve({
              ok: true,
              cancel: false,
              formSuccessfullySubmitted: !!formSuccessfullySubmitted,
            });
          },
          cancelCallback: () => {
            closeDialog();
            resolve({
              ok: false,
              cancel: true,
              formSuccessfullySubmitted: false,
            });
          },
        });
      else reject();
    });
};

export const useUsersPageProvider = () => {
  const editUserForm = useEditUserForm();
  const usersTable = useUsersTable();
  const mutateUser = useMutateUserGQL(editUserForm, usersTable);
  const submitUserEditorForm = useUserEditorFormSubmit(editUserForm, usersTable);
  const editUserConfirmationModal = useEditUserConfirmationModal(submitUserEditorForm);
  const payload = {
    usersTable,
    editUserForm: {
      ...editUserForm,
      editUserConfirmationModal,
      submitUserEditorForm,
    },
    GQL: {
      mutateUser,
    },
  };
  app.provide(IKUsersPage, payload);
  return payload;
};

export const useUsersPage = () => inject(IKUsersPage);
